import React, { useState } from "react";
import {
  Eye,
  EyeOff,
  Loader2,
  ChevronDown,
  X,
  AlertTriangle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useSignupScout } from "../../hooks/queries/useAuthMutations";

/* ───────────────────────────────────────────
   Constants
   ─────────────────────────────────────────── */
const COUNTRIES = [
  "Argentina",
  "Bolivia",
  "Brasil",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Cuba",
  "Ecuador",
  "El Salvador",
  "España",
  "Estados Unidos",
  "Guatemala",
  "Honduras",
  "México",
  "Nicaragua",
  "Panamá",
  "Paraguay",
  "Perú",
  "Puerto Rico",
  "República Dominicana",
  "Uruguay",
  "Venezuela",
];

const POSITION_OPTIONS = [
  "Presidente",
  "Director de academia",
  "Director deportivo",
  "Head Scout",
  "Scout",
  "Coach",
  "Representante",
];

/* ───────────────────────────────────────────
   Error Pop-up
   ─────────────────────────────────────────── */
const ErrorPopup = ({ isOpen, message, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-8 relative"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <X size={20} />
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle size={28} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Error en el registro
            </h3>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              {message}
            </p>
            <button
              onClick={onClose}
              className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3 rounded-lg transition-colors duration-200"
            >
              Entendido
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* ───────────────────────────────────────────
   Step Progress Bar
   ─────────────────────────────────────────── */
const StepProgressBar = ({ currentStep, totalSteps }) => (
  <div className="w-full flex gap-1 mb-6">
    {Array.from({ length: totalSteps }).map((_, i) => (
      <div
        key={i}
        className={`flex-1 h-1.5 rounded-full transition-colors duration-300 ${
          i <= currentStep ? "bg-blue-950" : "bg-gray-200"
        }`}
      />
    ))}
  </div>
);

/* ───────────────────────────────────────────
   Country Select Dropdown
   ─────────────────────────────────────────── */
const CountrySelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value || "País de la residencia*"}
        </span>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {COUNTRIES.map((country) => (
            <button
              key={country}
              type="button"
              onClick={() => {
                onChange(country);
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {country}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   Main: ScoutSignUpPage
   ═══════════════════════════════════════════ */
const ScoutSignUpPage = ({
  onSignUpComplete,
  onGoToLogin,
  onChangeAccountType,
}) => {
  const [step, setStep] = useState(0); // 0 = Step 1, 1 = Step 2

  // Step 1 fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [country, setCountry] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  // Step 2 fields
  const [fullName, setFullName] = useState("");
  const [position, setPosition] = useState(""); // Cargo
  const [organization, setOrganization] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  // General
  const [error, setError] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  const signup = useSignupScout();
  const isLoading = signup.isPending;

  /* ─── Step 1 validation ─── */
  const handleContinue = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password || !passwordConfirm || !country) {
      setError("Todos los campos son obligatorios.");
      return;
    }
    if (emailTaken) {
      setError("Este correo ya está registrado. Intenta iniciar sesión.");
      return;
    }
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setStep(1);
  };

  /* ─── Step 2 / Final submit ─── */
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError("");

    if (!fullName || !position || !organization) {
      setError("Todos los campos son obligatorios.");
      setShowErrorPopup(true);
      return;
    }
    if (!acceptTerms || !acceptPrivacy) {
      setError("Debes aceptar los términos y la política de privacidad.");
      setShowErrorPopup(true);
      return;
    }

    try {
      // Same `details` jsonb the supabase-era flow persisted (cargo/country/
      // consents included) — stored atomically by the signup endpoint.
      const consentTimestamp = new Date().toISOString();
      const data = await signup.mutateAsync({
        email,
        password,
        fullName,
        phone: "",
        organizationName: organization,
        // El backend resuelve (o crea) la academia por nombre dentro de la misma
        // transacción y vincula scouts.academy_id — la fuente de verdad del gate
        // de perfil completo. Se manda el NOMBRE y no un id porque /academies
        // exige sesión y acá todavía no hay ninguna.
        academyName: organization,
        details: {
          organization_info: {
            name: organization,
            type: "academy",
            address: "",
            country_phone: "",
            tax_id: "",
          },
          notifications: {
            email_alerts: true,
            push_notifications: true,
            sms_alerts: false,
            marketing: false,
          },
          public_profile: { url: "", is_visible: false },
          preferences: { language: "Español", timezone: "", country },
          subscription: {
            plan_name: "Free",
            next_payment: "",
            status: "active",
            benefits: [],
          },
          position,
          consents: {
            terms_accepted_at: acceptTerms ? consentTimestamp : null,
            privacy_accepted_at: acceptPrivacy ? consentTimestamp : null,
          },
        },
      });

      // Success — response carries the created scout row as `profile`
      if (onSignUpComplete) onSignUpComplete(data);
    } catch (err) {
      if (err?.code === "email_taken") {
        setEmailTaken(true);
        setError("Este correo ya está registrado. Intenta iniciar sesión.");
      } else if (err?.code === "rate_limited") {
        setError(
          "Has realizado demasiados intentos. Espera un momento antes de intentar de nuevo.",
        );
      } else if (err?.code === "API_UNREACHABLE") {
        setError("Error de conexión. Inténtalo de nuevo.");
      } else {
        setError(err?.message || "Error de conexión. Inténtalo de nuevo.");
      }
      setShowErrorPopup(true);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* ─── Main Content ─── */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Step Progress */}
          <StepProgressBar currentStep={step} totalSteps={2} />

          <AnimatePresence mode="wait">
            {/* ═══════════════════════════════════════
                STEP 1
               ═══════════════════════════════════════ */}
            {step === 0 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide italic mb-6">
                  Paso 1
                </h2>

                {/* Error */}
                {error && step === 0 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                <form onSubmit={handleContinue} className="space-y-4">
                  {/* Email */}
                  <div>
                    <input
                      type="email"
                      placeholder="Correo electrónico*"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailTaken(false);
                      }}
                      className={`w-full px-4 py-3.5 bg-gray-100 border rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all ${
                        emailTaken ? "border-red-400" : "border-gray-200"
                      }`}
                      required
                    />
                    {emailTaken && (
                      <p className="mt-1 text-xs text-red-500">
                        Este correo ya está registrado.
                      </p>
                    )}
                  </div>

                  {/* Password */}
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Contraseña*"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>

                  {/* Password Confirm */}
                  <div className="relative">
                    <input
                      type={showPasswordConfirm ? "text" : "password"}
                      placeholder="Confirmación de contraseña*"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswordConfirm(!showPasswordConfirm)
                      }
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPasswordConfirm ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>

                  {/* Country */}
                  <CountrySelect value={country} onChange={setCountry} />

                  {/* Continue */}
                  <div className="flex justify-center pt-4">
                    <button
                      type="submit"
                      className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3"
                    >
                      Continuar
                      <span className="text-base">✦</span>
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* ═══════════════════════════════════════
                STEP 2
               ═══════════════════════════════════════ */}
            {step === 1 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide italic mb-6">
                  Paso 2
                </h2>

                {/* Error */}
                {error && step === 1 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                <form
                  id="scout-signup-form"
                  onSubmit={handleCreateAccount}
                  className="space-y-4"
                >
                  {/* Full Name */}
                  <input
                    type="text"
                    placeholder="Nombre completo*"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                    required
                  />

                  {/* Cargo (Position/Role) */}
                  <select
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    className={`w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all ${
                      position ? "text-gray-900" : "text-gray-400"
                    }`}
                    required
                  >
                    <option value="">Cargo*</option>
                    {POSITION_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  {/* Organization.
                      Sin datalist de academias existentes, a diferencia de la
                      versión Supabase: GET /academies exige sesión y acá todavía
                      no hay ninguna, y publicarlo expondría el listado de
                      academias clientes a cualquiera. El backend igual hace
                      find-or-create case-insensitive, así que "Internacional FC"
                      y "internacional fc" siguen colapsando en una sola fila;
                      lo que se pierde es la sugerencia contra variantes tipo
                      "Internacional F.C.". */}
                  <input
                    type="text"
                    placeholder="Academia / club afiliado*"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                    required
                  />

                  {/* Terms */}
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-900 focus:ring-blue-900 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">
                      Acepto términos y condiciones
                    </span>
                  </label>

                  {/* Privacy */}
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acceptPrivacy}
                      onChange={(e) => setAcceptPrivacy(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-900 focus:ring-blue-900 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">
                      Política de privacidad
                    </span>
                  </label>

                  {/* Create Account */}
                  <div className="flex justify-center pt-4">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>
                          Crear cuenta
                          <span className="text-base">✦</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Back to step 1 */}
                <button
                  onClick={() => {
                    setError("");
                    setStep(0);
                  }}
                  className="w-full text-center mt-4 text-sm text-gray-500 hover:text-blue-900 transition-colors"
                >
                  ← Volver al paso anterior
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Already have account */}
          <button
            type="button"
            onClick={onChangeAccountType}
            className="mt-6 w-full text-center text-sm text-gray-500 transition-colors hover:text-blue-900"
          >
            Cambiar tipo de cuenta
          </button>
          <p className="mt-3 text-center text-sm text-gray-500">
            ¿Ya tienes cuenta?{" "}
            <button
              onClick={onGoToLogin}
              className="text-gray-700 font-medium hover:text-blue-900 underline underline-offset-2 transition-colors"
            >
              Inicia sesión
            </button>
          </p>
        </div>
      </div>

      {/* Error Popup */}
      <ErrorPopup
        isOpen={showErrorPopup}
        message={error}
        onClose={() => setShowErrorPopup(false)}
      />
    </div>
  );
};

export default ScoutSignUpPage;
